// WOS-BUNDLE: favorites live on the server (`favorites.list / add / remove / reorder`), one list
// per person, in their order, holding only what they may still open (Work OS `favoritesFor`).
// Stars on spaces, boards, docs, tasks and channels toggle at once and undo themselves with a
// "Couldn't update favorites" toast when the server refuses (Work OS location-header). The first
// time a person's server list loads, the stars this browser kept before (`localStorage
// companyos.work.favorites.{companyId}`, M2-07) are added to it once, then forgotten. An API that
// does not serve the routes yet keeps using the browser's list.
import { toast } from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import { OPERATOR_ONLY_REASON, useOperatorOnly } from "../operator-only";
import { type Favorite, toggleFavorite } from "./arrangement";
import { pendingAsNull } from "./work-queries";

export type FavoriteView = NonNullable<
  Awaited<ReturnType<Api["client"]["favorites"]["list"]>>
>["data"][number];

/** Under Work: a Work write (a doc or board deleted) refetches the list, which drops it. */
export const favoritesKey = (co: string) => ["company", co, "work", "favorites"] as const;

/** The caller's stars, or null while the API does not serve favorites yet. */
export const favoritesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: favoritesKey(co),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.favorites.list({ params: { co } })))?.data ?? null,
    staleTime: 60_000,
  });

const LEGACY = (co: string) => `companyos.work.favorites.${co}`;

/** The stars this browser kept before favorites moved to the server (none when unreadable). */
export function readLegacyFavorites(co: string): Favorite[] {
  try {
    const raw = window.localStorage.getItem(LEGACY(co));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list)
      ? list.filter(
          (f): f is Favorite =>
            typeof f === "object" &&
            f !== null &&
            typeof (f as Favorite).id === "string" &&
            ["space", "board", "page", "task", "channel"].includes((f as Favorite).kind),
        )
      : [];
  } catch {
    return [];
  }
}

function forgetLegacy(co: string) {
  try {
    window.localStorage.removeItem(LEGACY(co));
  } catch {}
}

/** Companies whose browser list was moved this page load (it runs once). */
const migrated = new Set<string>();

/**
 * Moves the browser's old stars to the server: each one not starred there yet is added (in its
 * old order, after the server's), then the browser forgets them. Stars the server refuses (gone,
 * no longer visible) are dropped, like the list drops them. Returns how many were added.
 */
export async function migrateLegacyFavorites(
  api: Api,
  co: string,
  server: readonly { kind: string; id: string }[],
): Promise<number> {
  const local = readLegacyFavorites(co);
  let added = 0;
  for (const f of local) {
    if (server.some((s) => s.kind === f.kind && s.id === f.id)) continue;
    try {
      await api.client.favorites.add({ params: { co }, body: { kind: f.kind, object_id: f.id } });
      added += 1;
    } catch {}
  }
  forgetLegacy(co);
  return added;
}

/** Writes the stars in a new order (the sidebar's drag, keys and menu moves). */
export function reorderFavorites<T extends { kind: string; id: string }>(
  list: readonly T[],
  from: number,
  to: number,
): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  if (!moved) return next;
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

const failed = () => toast.add({ title: "Couldn't update favorites", type: "warning" });

export function useFavorites(companyId: string) {
  const { api } = useRouteContext({ from: "__root__" });
  return useFavoritesOf(api, companyId);
}

export function useFavoritesOf(api: Api, co: string) {
  const qc = useQueryClient();
  // OPERATOR-ONLY-UX: favorites are a person member's; an operator-only caller has none.
  const operatorOnly = useOperatorOnly(api, co);
  const q = useQuery({ ...favoritesQuery(api, co), enabled: !operatorOnly });
  const server = q.data;
  // The browser's stars (the list while the API has no favorites routes).
  const legacy = server === null ? readLegacyFavorites(co) : [];

  useEffect(() => {
    if (!server || migrated.has(co)) return;
    migrated.add(co);
    if (!readLegacyFavorites(co).length) return;
    void migrateLegacyFavorites(api, co, server).then((n) => {
      if (n) void qc.invalidateQueries({ queryKey: favoritesKey(co) });
    });
  }, [api, co, server, qc]);

  const set = (fn: (list: FavoriteView[]) => FavoriteView[]) =>
    qc.setQueryData<FavoriteView[] | null>(favoritesKey(co), (d) => (d ? fn(d) : d));
  const onFailure = (_e: unknown, _v: unknown, rollback: () => void) => {
    rollback();
    failed();
  };

  const star = useOptimisticMutation({
    keys: () => [favoritesKey(co)],
    apply: (v: { f: Favorite; title: string; on: boolean }) =>
      set((list) =>
        v.on
          ? list.some((x) => x.kind === v.f.kind && x.id === v.f.id)
            ? list
            : [
                ...list,
                {
                  kind: v.f.kind,
                  id: v.f.id,
                  title: v.title,
                  position: list.length,
                  space_id: null,
                  board_id: null,
                },
              ]
          : list.filter((x) => !(x.kind === v.f.kind && x.id === v.f.id)),
      ),
    mutationFn: async (v) => {
      if (v.on)
        await api.client.favorites.add({
          params: { co },
          body: { kind: v.f.kind, object_id: v.f.id },
        });
      else await api.client.favorites.remove({ params: { co, kind: v.f.kind, id: v.f.id } });
    },
    settle: () => [favoritesKey(co)],
    onFailure,
  });

  const order = useOptimisticMutation({
    keys: () => [favoritesKey(co)],
    apply: (next: FavoriteView[]) => set(() => next.map((f, i) => ({ ...f, position: i }))),
    mutationFn: (next) =>
      api.client.favorites.reorder({
        params: { co },
        body: { items: next.map((f) => ({ kind: f.kind, object_id: f.id })) },
      }),
    reconcile: (d) => qc.setQueryData(favoritesKey(co), d.data),
    onFailure,
  });

  const list: { kind: Favorite["kind"]; id: string; title: string | null }[] = server
    ? server.map((f) => ({ kind: f.kind, id: f.id, title: f.title }))
    : legacy.map((f) => ({ ...f, title: null }));
  const has = (f: Favorite) => list.some((x) => x.kind === f.kind && x.id === f.id);
  return {
    /** Server rows (titles included), or the browser's stars (no titles) on an older API. */
    list,
    views: server ?? [],
    /** Whether the stars are the server's (the sidebar's reorder needs it). */
    server: Boolean(server),
    has,
    toggle: (f: Favorite, title = "") => {
      if (operatorOnly) {
        toast.add({ title: OPERATOR_ONLY_REASON, type: "warning" });
        return;
      }
      if (server === null) {
        const next = toggleFavorite(readLegacyFavorites(co), f);
        try {
          window.localStorage.setItem(LEGACY(co), JSON.stringify(next));
        } catch {}
        void qc.invalidateQueries({ queryKey: favoritesKey(co) });
        return;
      }
      star.mutate({ f, title, on: !has(f) });
    },
    reorder: (next: FavoriteView[]) => order.mutate(next),
  };
}
